import type { Event, TrainingClassSession } from '../types';
import { useMemo } from 'react';
import { getCurrentUser } from '~/common/explicit-exports';
import { isMultiSession } from '~/common';
import {
  getCanOnlyWaitlistEvent,
  getIsLearnerAttendedSession,
  getIsLearnerRegisteredForEventSeries,
  getIsLearnerWaitlistedForEventSeries,
  getIsLearnerRegisteredForSession,
  getIsLearnerWaitlisted,
  getIsSessionFull,
  getIsWaitlistFull,
  sessionHasEnded,
  getIsLearnerAbsentSession,
  getIsDeadlinePast,
} from '../utils';

export const useEventSessionDetails = (event: Event, session: TrainingClassSession) => {
  const currentUser = getCurrentUser();

  const { attendeesMarkOwnAttendance, canRegister: canLearnerRegister } = session;

  const isEventSeries = isMultiSession(event);

  const isLearnerRegistered = useMemo(() => {
    if (isEventSeries) {
      return getIsLearnerRegisteredForEventSeries(currentUser.id, event);
    }

    return getIsLearnerRegisteredForSession(currentUser.id, session.trainingClassSessionAttendees);
  }, [isEventSeries, currentUser.id, session.trainingClassSessionAttendees, event]);

  const isSessionEnded = useMemo(() => sessionHasEnded(session), [session]);

  const isLearnerAttended = useMemo(
    () => getIsLearnerAttendedSession(currentUser.id, session.trainingClassSessionAttendees),
    [currentUser.id, session],
  );

  const wasLearnerAbsent = useMemo(
    () => isSessionEnded && getIsLearnerAbsentSession(currentUser.id, session.trainingClassSessionAttendees),
    [isSessionEnded, currentUser.id, session.trainingClassSessionAttendees],
  );

  const isLearnerWaitlisted = useMemo(() => {
    if (isEventSeries) {
      return getIsLearnerWaitlistedForEventSeries(currentUser.id, event);
    }
    return getIsLearnerWaitlisted(currentUser.id, session.trainingClassSessionAttendees);
  }, [currentUser.id, event, isEventSeries, session.trainingClassSessionAttendees]);

  const isSessionFull = getIsSessionFull(session, event.maxAttendees);

  const canOnlyWaitlist = getCanOnlyWaitlistEvent(event);

  const isWaitlistFull = getIsWaitlistFull(event, session);

  const isDeadlinePast = getIsDeadlinePast(event, session);

  const isWaitlistAvailable = event.waitlistEnabled && !isWaitlistFull;

  const canLearnerMarkAttendance = isSessionEnded && attendeesMarkOwnAttendance && !isLearnerAttended;

  const canLearnerUnmarkAttendance = isSessionEnded && attendeesMarkOwnAttendance && isLearnerAttended;

  const mustJoinWaitlist = isSessionFull || canOnlyWaitlist;

  const canLearnerJoinWaitlist = mustJoinWaitlist && isWaitlistAvailable;

  const canLearnerNotJoinWaitlist = mustJoinWaitlist && !isWaitlistAvailable;

  return {
    attendeesMarkOwnAttendance,
    isLearnerRegistered,
    isSessionEnded,
    isLearnerAttended,
    isLearnerWaitlisted,
    isSessionFull,
    canOnlyWaitlist,
    isDeadlinePast,
    isEventSeries,
    isWaitlistFull,
    isWaitlistAvailable,
    canLearnerMarkAttendance,
    canLearnerUnmarkAttendance,
    canLearnerJoinWaitlist,
    canLearnerNotJoinWaitlist,
    canLearnerRegister,
    wasLearnerAbsent,
  };
};
